import React, { useState, useEffect, useContext } from "react";
import { DataContext } from "../context/contextApi";
import VideoCard from "../components/VideoCard";
import ShimmerVideoCard from "../shared/ShimmerVideoCard";
import UseOnline from "../utils/UseOnline";
import OfflineComponent from "../shared/OfflineComponent";
import { getFavorites } from "../utils/favorites";
import { toAppVideo, getThumbnailUrl } from "../utils/videoHelper";
import { Helmet } from "react-helmet-async";

const Favorites = () => {
  const { loading, firebaseVideos, firebaseLoading } = useContext(DataContext);
  const [favoriteVideos, setFavoriteVideos] = useState([]);
  const isOnline = UseOnline();

  useEffect(() => {
    document.getElementById("root").classList.remove("custom-h");
  }, []);

  useEffect(() => {
    // adapt to new favorites shape and listen for updates
    const loadFavorites = () => {
      const entries = getFavorites();

      if (entries.length === 0) {
        setFavoriteVideos([]);
        return;
      }

      // helper to build a safe fallback video object
      const safeFromEntry = (e) => {
        // Prefer stored payload
        if (e.video) return e.video;
        // Try to derive from URL/ID
        try {
          const fromUrl = toAppVideo({ url: e.id.indexOf('%3A') !== -1 ? decodeURIComponent(e.id) : e.id });
          if (fromUrl) return fromUrl;
        } catch (err) { /* ignore */ }

        // generic fallback
        return {
          videoId: e.id,
          originalUrl: e.id,
          title: e.id || '(No title)',
          thumbnails: [{ url: getThumbnailUrl(e.id) || '/default-thumbnail.svg' }],
          stats: { views: 0, likes: 0 },
          publishedTimeText: ''
        };
      };

      if (!firebaseLoading && firebaseVideos && firebaseVideos.length > 0) {
        const favVideos = entries.map((e) => {
          const found = firebaseVideos.find(v => v.videoId === e.id);
          // Prefer RTDB record if available and more authoritative, else stored payload, else derived
          return found || e.video || safeFromEntry(e);
        }).filter(Boolean);

        setFavoriteVideos(favVideos.map(v => (v)));
      } else if (firebaseLoading) {
        setFavoriteVideos([]);
      } else {
        // No firebase videos available: use any stored video payloads, or derived minimal entries
        const favVideos = entries.map(e => safeFromEntry(e));
        setFavoriteVideos(favVideos);
      }
    };

    loadFavorites();

    const onUpdated = () => loadFavorites();
    window.addEventListener('favorites:updated', onUpdated);
    return () => window.removeEventListener('favorites:updated', onUpdated);
  }, [firebaseVideos, firebaseLoading]);

  return (
    <div className="flex flex-1">
      <Helmet>
        <title>My Favorites - A2Z anime & Cartoons</title>
        <meta name="description" content="Your saved favorite videos on A2Z anime & Cartoons." />
        <link rel="canonical" href={window.location.origin + window.location.pathname} />
      </Helmet>
      {!isOnline && (
        <div className="grow w-full h-full overflow-y-auto bg-black">
          <OfflineComponent />
        </div>
      )}
      {isOnline && (
        <div className="grow w-full min-h-screen overflow-y-auto bg-white dark:bg-black">
          <div className="w-full bg-transparent px-5 py-6">
            <h1 className="text-2xl font-bold text-black dark:text-white mb-6">
              My Favorites
            </h1>

            {favoriteVideos.length === 0 && !firebaseLoading ? (
              <div className="text-center py-12">
                <p className="text-lg text-black/[0.7] dark:text-white/[0.7] mb-4">
                  No favorite videos yet
                </p>
                <p className="text-sm text-black/[0.5] dark:text-white/[0.5]">
                  Click the bookmark icon on any video to save it here
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {(loading || firebaseLoading) ? (
                  Array(12)
                    .fill("")
                    .map((e, index) => (
                      <ShimmerVideoCard key={index} />
                    ))
                ) : (
                  favoriteVideos.map((video, idx) => (
                    <VideoCard key={video.videoId || idx} video={video} />
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Favorites;